import axios, { type AxiosError, type AxiosRequestConfig, type AxiosResponse } from 'axios';

import { getSafeCode, getSafeDetails, getSafeName, isRecord, normalizePath } from '../telemetrySafe';

type NetworkStatusLabel = 'online' | 'offline' | 'unknown';

interface AxiosErrorGroupingOptions {
  networkStatus?: NetworkStatusLabel;
}

interface AxiosErrorMetadata {
  axiosErrorCode: string | undefined;
  clientTid: string | undefined;
  fieldErrors: string[] | undefined;
  host: string;
  itemErrorCodes: string[] | undefined;
  message: string;
  method: string;
  networkStatus: NetworkStatusLabel | undefined;
  parameterName: string | undefined;
  path: string | undefined;
  qcsErrorCode: string | undefined;
  qcsTraceId: string | undefined;
  serverErrorClass: string | undefined;
  status: number | undefined;
}

type RequestConfig = Pick<AxiosRequestConfig, 'allowAbsoluteUrls' | 'baseURL' | 'headers' | 'method' | 'url'>;

/** The parts of a failed request this module reads. An AxiosError has them all. */
type RequestFailure = Partial<Pick<AxiosError, 'code' | 'message'>> & {
  config?: RequestConfig;
  response?: Partial<Pick<AxiosResponse, 'data' | 'headers' | 'status'>>;
};

interface RequestIds {
  clientTid: string | undefined;
  qcsTraceId: string | undefined;
}

interface AxiosErrorGroupingResult {
  groupingHash: string;
  context: string | undefined;
  metadata: AxiosErrorMetadata;
}

/** Structural subset of a BugSnag Event; satisfied by both @bugsnag/js and @bugsnag/react-native. */
interface GroupableBugsnagEvent {
  addMetadata(section: string, values: Record<string, unknown>): void;
  context?: string;
  groupingHash?: string;
  originalError?: unknown;
}

interface ApplyAxiosErrorGroupingOptions extends AxiosErrorGroupingOptions {
  /** Platform extras merged into the `network` metadata tab (e.g. the screen or page that context previously carried). */
  extraMetadata?: Record<string, unknown>;
}

const UNKNOWN_HOST = 'unknown-host';
const UNKNOWN_PATH = 'unknown-path';
const MAX_LISTED = 10;
const SERVER_ERROR_CLASS = /^[A-Za-z_$][\w.$]*(?:Exception|Error)(?=:|$)/;

// Web's axiosFactory rebrands offline network failures into a plain `ClientError` object
// (a spread of the original AxiosError, so `config` survives); group it like an AxiosError.
const isGroupableRequestError = (error: unknown): error is AxiosError =>
  axios.isAxiosError(error) ||
  (typeof error === 'object' &&
    error !== null &&
    'name' in error &&
    error.name === 'ClientError' &&
    'code' in error &&
    error.code === 'CLIENT_OFFLINE');

const parseRequestUrl = (config: RequestConfig | undefined): URL | undefined => {
  const url = typeof config?.url === 'string' ? config.url : undefined;
  const baseURL = typeof config?.baseURL === 'string' ? config.baseURL : undefined;
  if (!url && !baseURL) return undefined;
  try {
    const fullUrl = axios.getUri({ allowAbsoluteUrls: config?.allowAbsoluteUrls, baseURL, url });
    return fullUrl ? new URL(fullUrl, `https://${UNKNOWN_HOST}`) : undefined;
  } catch {
    return undefined;
  }
};

const getHeader = (headers: unknown, name: string): string | undefined =>
  isRecord(headers) ? getSafeCode(headers[name]) : undefined;

/**
 * The two ids that find a request in Splunk: clientTid in qcs_ingress, and traceId in the service logs. A response with
 * no qcsTraceId never reached the gateway, except on web, which can't read that header until the gateway exposes it.
 */
export const getRequestIds = ({ config, response }: { config?: unknown; response?: unknown }): RequestIds => ({
  clientTid: getHeader(isRecord(config) ? config.headers : undefined, 'client-tid'),
  qcsTraceId: getHeader(isRecord(response) ? response.headers : undefined, 'qcs-trace-id'),
});

// QCS lists its errors, while biller and problem+json bodies put the code at the top, beside fieldErrors. A bare number
// there says nothing the status doesn't. On a success, `code` is data, and it can be a passcode.
const getBodyCode = (data: unknown, status: number | undefined): string | undefined =>
  isRecord(data) && (status ?? 0) >= 400
    ? [data.code, data.errorCode].map(getSafeCode).find((code) => code && /[A-Za-z]/.test(code))
    : undefined;

const getItemErrors = (data: unknown): unknown[] =>
  isRecord(data) && Array.isArray(data.items)
    ? data.items.flatMap((item: unknown) => (isRecord(item) && Array.isArray(item.errors) ? item.errors : []))
    : [];

/** The distinct error codes of a batch response's failed items. */
export const getItemErrorCodes = (data: unknown): string[] | undefined => {
  const codes = getItemErrors(data).flatMap((error) => getSafeCode(isRecord(error) ? error.code : undefined) ?? []);
  return codes.length ? [...new Set(codes)].slice(0, MAX_LISTED) : undefined;
};

// QCS lists rejected fields on the error's extData, or at the top of a problem+json body. Names and codes only: the
// message beside them can quote what the customer typed.
const getFieldErrors = (...sources: unknown[]): string[] | undefined => {
  const fieldErrors = sources
    .flatMap((source) => (isRecord(source) && Array.isArray(source.fieldErrors) ? source.fieldErrors : []))
    .flatMap((fieldError: unknown) => {
      const field = isRecord(fieldError) ? getSafeName(fieldError.field) : undefined;
      return isRecord(fieldError) && field ? [[field, getSafeCode(fieldError.code)].filter(Boolean).join(':')] : [];
    });
  return fieldErrors.length ? fieldErrors.slice(0, MAX_LISTED) : undefined;
};

// Only the class name: the text after it can hold ids and values.
const getServerErrorClass = (extData: unknown): string | undefined => {
  const exception = isRecord(extData) ? extData.exception : undefined;
  return typeof exception === 'string' ? exception.match(SERVER_ERROR_CLASS)?.[0] : undefined;
};

const normalizeMessage = (message: string): string =>
  message.replace(/^timeout of \d+ms exceeded$/i, 'timeout exceeded');

/** What a report may say about a failed request: its route, its codes and its ids. Never bodies, params or headers. */
export const getRequestFailureMetadata = (
  { code, config, message, response }: RequestFailure,
  options?: AxiosErrorGroupingOptions,
): AxiosErrorMetadata => {
  const url = parseRequestUrl(config);
  const data: unknown = response?.data;
  const itemErrors = getItemErrors(data);
  const errors = isRecord(data) && Array.isArray(data.errors) ? data.errors : [];
  const firstError: unknown = errors[0] ?? itemErrors[0];
  const extData = isRecord(firstError) ? firstError.extData : undefined;

  return {
    ...getRequestIds({ config, response }),
    axiosErrorCode: typeof code === 'string' ? code : undefined,
    fieldErrors: getFieldErrors(extData, data),
    host: url?.hostname || UNKNOWN_HOST,
    itemErrorCodes: getItemErrorCodes(data),
    message: normalizeMessage(typeof message === 'string' && message ? message : 'unknown'),
    method: typeof config?.method === 'string' ? config.method.toUpperCase() : 'GET',
    networkStatus: options?.networkStatus,
    parameterName: getSafeName(isRecord(extData) ? extData.parameterName : undefined),
    // A relative URL has no host to judge.
    path: url ? normalizePath(url.pathname, url.hostname === UNKNOWN_HOST ? undefined : url.hostname) : undefined,
    qcsErrorCode:
      getSafeCode(isRecord(firstError) ? firstError.code : undefined) ?? getBodyCode(data, response?.status),
    serverErrorClass: getServerErrorClass(extData),
    status: typeof response?.status === 'number' ? response.status : undefined,
  };
};

/** Build stable BugSnag grouping data for raw Axios errors without copying request payloads or identifiers. */
export const getAxiosErrorGrouping = (
  error: unknown,
  options?: AxiosErrorGroupingOptions,
): AxiosErrorGroupingResult | null => {
  if (!isGroupableRequestError(error)) return null;

  const metadata = getRequestFailureMetadata(error, options);
  const { axiosErrorCode, host, message, method, path, qcsErrorCode, status } = metadata;
  const detail = qcsErrorCode ?? axiosErrorCode;
  // `tag` suffixes the human-readable context and keeps the failure message; `kind` feeds the
  // grouping hash, so it drops the free-text message whenever a stable code exists.
  const tag = status === undefined ? `${message}:${detail ?? 'unknown'}` : `${status}:${detail ?? 'unknown'}`;
  const kind = status === undefined ? (detail ?? message) : tag;

  return {
    groupingHash: `axios-network-error:${method}:${host}:${path ?? UNKNOWN_PATH}:${kind}`,
    context: path ? `${path} [${tag}]` : undefined,
    metadata,
  };
};

/** Metadata tabs for the values handed to `assert`: their safe fields, plus a summary when one is a failed request. */
export const getAssertDetails = (values: readonly unknown[]): Record<string, Record<string, unknown>> => {
  const custom: Record<string, unknown> = Object.assign({}, ...values.map((value) => getSafeDetails(value)));
  const failure = values.find(isGroupableRequestError);
  return {
    ...(Object.keys(custom).length > 0 && { custom }),
    ...(failure && { network: { ...getRequestFailureMetadata(failure) } }),
  };
};

/**
 * Group a raw Axios error (an escaped saga rejection) per endpoint + failure kind. Without this
 * it groups on axios-internal stack frames, collapsing every endpoint into one giant report that
 * forks on each axios upgrade. Rewrites context to the endpoint (pass what it previously carried
 * via `extraMetadata`) and never overrides an explicitly set groupingHash.
 */
export const applyAxiosErrorGrouping = (
  event: GroupableBugsnagEvent,
  options?: ApplyAxiosErrorGroupingOptions,
): void => {
  if (event.groupingHash) return;
  const grouping = getAxiosErrorGrouping(event.originalError, options);
  if (!grouping) return;

  event.groupingHash = grouping.groupingHash;
  event.addMetadata('network', { ...grouping.metadata, ...options?.extraMetadata });
  if (grouping.context) {
    event.context = grouping.context;
  }
};
